import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  ISkuContentRuleSku,
  SkuContentRuleSkuRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import Field from "components/Field";
import SelectSku from "components/SelectSku";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 30px",
  },
  paper: {
    marginTop: 10,
  },
});

type SkuRuleDetailSkuContentFormProps = {
  parentSkuId: string;
  skuRuleId: string;
  skuContentRuleSku: ISkuContentRuleSku | null | undefined;
  dialogTitleKey: string;
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
};

type SkuRuleDetailSkuContentFormValues = {
  id: string | undefined;
  skuId: string | undefined;
};

const SkuRuleDetailSkuContentForm = ({
  parentSkuId,
  skuRuleId,
  skuContentRuleSku,
  dialogTitleKey,
  onClose,
  onCreate,
  onUpdate,
}: SkuRuleDetailSkuContentFormProps) => {
  const { skuService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [skuTitle, setSkuTitle] = useState<string | undefined>("");
  const [skuId, setSkuId] = useState<string | undefined>("");
  const init = useMemo(
    () => ({
      skuRuleId,
      ...pick(skuContentRuleSku ?? {}, ["id", "skuId"]),
    }),
    [skuContentRuleSku, skuRuleId]
  );

  const onSubmit = async (values: SkuRuleDetailSkuContentFormValues) => {
    try {
      const response = await skuService.saveSkuContentRuleSku(
        parentSkuId,
        skuRuleId,
        SkuContentRuleSkuRequest.fromJS({
          id: skuContentRuleSku?.id,
          skuId: skuId!,
        })
      );

      !values.id ? onCreate() : onUpdate();

      onClose();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">
            <T id={dialogTitleKey} />
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="skuId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectSku
                        {...input}
                        required
                        hasSkuContents={true}
                        label={t({ id: "skuLabel" })}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                        onSkuChange={(sku) => {
                          setSkuTitle(sku?.title ?? "");
                          setSkuId(sku?.id ?? "");
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Grid item xs={12}>
                <TextField
                  disabled={true}
                  variant="standard"
                  fullWidth
                  label={t({ id: "skuTitleColumnLabel" })}
                  value={skuTitle}
                />
              </Grid>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onClose}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || !values.skuId}
            >
              {t({ id: "saveFilterLabel" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default SkuRuleDetailSkuContentForm;
